export const AUTH_ERROR = {
  EMAIL_NOT_FOUND: 'EMAIL_NOT_FOUND',
  INVALID_PASSWORD: 'INVALID_PASSWORD',
  TOO_MANY_ATTEMPTS: 'TOO_MANY_ATTEMPTS_TRY_LATER',
  INVALID_CODE: 'INVALID_CODE',
  CODE_EXPIRED: 'CODE_EXPIRED',
  UNEXPECTED: 'UNEXPECTED',
  USER_NOT_CONFIRMED: 'USER_NOT_CONFIRMED',
  PASSWORD_RESET_REQUIRED: 'PASSWORD_RESET_REQUIRED',
  USER_DISABLED: 'USER_DISABLED',
} as const;

export type AuthErrorCode = typeof AUTH_ERROR[keyof typeof AUTH_ERROR];

export type AuthProviderError = Error & {
  code?: string;
  __type?: string;
};

const messageIncludes = (
  value: string | undefined,
  search: string,
): boolean => {
  if (!value) {
    return false;
  }

  return value.toLowerCase().includes(search.toLowerCase());
};

const normalizeAuthError = (error: unknown): AuthErrorCode => {
  const providerError = (error || {}) as AuthProviderError;
  const codeOrType =
    providerError.code ?? providerError.__type ?? providerError.name ?? '';
  const message = providerError.message ?? '';

  if (
    codeOrType === 'CodeMismatchException' ||
    messageIncludes(message, 'invalid code')
  ) {
    return AUTH_ERROR.INVALID_CODE;
  }

  if (
    codeOrType === 'ExpiredCodeException' ||
    messageIncludes(message, 'expired code')
  ) {
    return AUTH_ERROR.CODE_EXPIRED;
  }

  if (
    codeOrType === 'UserNotFoundException' ||
    codeOrType === 'ResourceNotFoundException'
  ) {
    return AUTH_ERROR.EMAIL_NOT_FOUND;
  }

  if (codeOrType === 'UserNotConfirmedException') {
    return AUTH_ERROR.USER_NOT_CONFIRMED;
  }

  if (codeOrType === 'PasswordResetRequiredException') {
    return AUTH_ERROR.PASSWORD_RESET_REQUIRED;
  }

  if (codeOrType === 'UserDisabledException') {
    return AUTH_ERROR.USER_DISABLED;
  }

  if (
    codeOrType === 'NotAuthorizedException' ||
    messageIncludes(message, 'incorrect username or password')
  ) {
    return AUTH_ERROR.INVALID_PASSWORD;
  }

  if (
    codeOrType === 'TooManyFailedAttemptsException' ||
    codeOrType === 'LimitExceededException' ||
    messageIncludes(message, 'too many') ||
    messageIncludes(message, 'exceeded')
  ) {
    return AUTH_ERROR.TOO_MANY_ATTEMPTS;
  }

  return (codeOrType as AuthErrorCode) || AUTH_ERROR.UNEXPECTED;
};

export class AuthError extends Error {
  code: AuthErrorCode;

  constructor(code: AuthErrorCode, message?: string) {
    super(message ?? code);
    this.name = 'AuthError';
    this.code = code;
  }
}

export const createAuthError = (
  error: unknown,
  fallback?: string,
): AuthError => {
  const normalizedCode = normalizeAuthError(error);
  const message =
    (error as Error)?.message ??
    (fallback ? `${fallback}: ${normalizedCode}` : normalizedCode);
  return new AuthError(normalizedCode, message);
};
